import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { ErrorGenericSchema, MemorySchema, YYYYMMDDSchema } from '../../models';

extendZodWithOpenApi(z);
const c = initContract();

/** Most memories one `getUserMemories` response carries; past it the response is `truncated`. */
export const USER_MEMORIES_DAY_CAP = 5000;

/**
 * INTERNAL-ONLY TIMELY MEMORIES CONTRACT
 *
 * `getUserMemories` reads a whole day of any user's activity by Timely id, a
 * cross-user PII read, so it is reachable ONLY on the internal `/api` mount.
 * This router is referenced solely by the internal `internalApiSpec` in
 * `apps/integration-cloud/src/internal/api/index.ts`; it is deliberately NOT
 * assembled into the public `apiSpec` (`src/api.ts`), so the public app never
 * routes the path (proved by a public-app 404 negative test).
 *
 * Auth is handler-local (shared-secret `INTERNAL_TIMELY_WEBHOOK_API_KEY` via
 * `apiRouteInitializer`), identical to the other internal webhooks.
 */
export const internalApiTimelyMemories = c.router({
  getUserMemories: {
    method: 'GET',
    path: `/1.0/timely/memories/by-user`,
    query: z.object({
      timely_user_id: z.coerce.number().int().min(1).openapi({
        description: 'The Timely user ID whose memories should be returned',
        example: 2302306,
      }),
      date: YYYYMMDDSchema.openapi({
        description:
          'The calendar day (YYYY-MM-DD) to return memories for, resolved in `time_zone`.',
        example: '2024-01-15',
      }),
      time_zone: z.string().min(1).openapi({
        description:
          'IANA time zone name used to anchor `date` to a local-day window (e.g. "America/Los_Angeles"). Fixed offsets ("UTC+7") and host-relative names ("local", "system") are rejected. Memories are matched against the user-local day, not a UTC day, so activity that straddles the UTC boundary is attributed to the correct local date.',
        example: 'America/Los_Angeles',
      }),
    }),
    responses: {
      200: z.object({
        memories: z.array(MemorySchema).openapi({
          description: `The memories for the user on the requested day, newest first, at most ${USER_MEMORIES_DAY_CAP}.`,
        }),
        truncated: z.boolean().openapi({
          description: `True when the day holds more than ${USER_MEMORIES_DAY_CAP} memories; \`memories\` then holds the newest ${USER_MEMORIES_DAY_CAP} and omits the earliest part of the day.`,
        }),
      }),
      400: ErrorGenericSchema.describe(
        '`date` is not a real calendar day, `time_zone` is not an IANA zone name, or `date` does not exist in `time_zone`.'
      ),
      504: ErrorGenericSchema.describe(
        'The memories query exceeded its statement timeout and was canceled server-side. ' +
          'Carries `Retry-After`.'
      ),
    },
    summary: "Fetch a TIC user's memories for a single day",
    description: `Returns the memories for a specific TIC user (resolved from \`timely_user_id\`) whose started_at falls on the requested \`date\` in \`time_zone\`: the half-open window from local midnight to the next local midnight, so a day that crosses a DST change is 23 or 25 hours long.

If no TIC user is found for the given \`timely_user_id\`, returns an empty \`memories\` array with a 200 status.

**Mount**: internal app only (\`/api/1.0/timely/memories/by-user\`); the public app does not route it.

**Authentication**: Requires \`INTERNAL_TIMELY_WEBHOOK_API_KEY\` in Authorization header.`,
  },
});
